import { useState } from 'react';
import { supabase } from '../AuthProvider';
import { useAuth } from '../../../hooks/useAuth';
import posthog from 'posthog-js';
import Logger from '@app/utils/logger';

export interface UserInfoFormData {
  displayName: string;
  country: string;
  howFoundUs: string;
  userType: 'private' | 'business' | '';
  companyName: string;
  roleDescription: string;
}

export interface InitialUserData {
  displayName?: string;
  country?: string;
  howFoundUs?: string;
  userType?: 'private' | 'business' | '';
  companyName?: string;
  roleDescription?: string;
}

export function useUserInfoForm(initialData: InitialUserData = {}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<UserInfoFormData>({
    displayName: initialData.displayName || '',
    country: initialData.country || '',
    howFoundUs: initialData.howFoundUs || '',
    userType: initialData.userType || '',
    companyName: initialData.companyName || '',
    roleDescription: initialData.roleDescription || '',
  });

  const [loading, setLoading] = useState(false);
  const { refreshUser } = useAuth();

  const updateField = (field: keyof UserInfoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate step 1 fields
  const validateStep1 = (): string | null => {
    if (!formData.displayName.trim()) {
      return 'Display name is required';
    }

    if (!formData.country) {
      return 'Country is required';
    }

    return null;
  };

  // Validate step 2 fields
  const validateStep2 = (): string | null => {
    if (!formData.howFoundUs) {
      return 'Please tell us how you found us';
    }

    if (!formData.userType) {
      return 'User type is required';
    }

    if (formData.userType === 'business' && !formData.companyName.trim()) {
      return 'Company name is required for business accounts';
    }

    if (!formData.roleDescription) {
      return 'Role description is required';
    }

    return null;
  };

  const validateForm = (): string | null => {
    const step1Error = validateStep1();
    if (step1Error) return step1Error;

    const step2Error = validateStep2();
    if (step2Error) return step2Error;

    return null;
  };

  // Check if current step is valid
  const isCurrentStepValid =
    currentStep === 1
      ? formData.displayName.trim() && formData.country
      : formData.howFoundUs &&
        formData.userType &&
        (formData.userType === 'private' || formData.companyName.trim()) &&
        formData.roleDescription;

  const isFormValid =
    formData.displayName.trim() &&
    formData.country &&
    formData.howFoundUs &&
    formData.userType &&
    (formData.userType === 'private' || formData.companyName.trim()) &&
    formData.roleDescription;

  const nextStep = () => {
    if (currentStep === 1 && isCurrentStepValid) {
      setCurrentStep(2);
    }
  };

  const prevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const submitForm = async (): Promise<string | null> => {
    const validationError = validateForm();
    if (validationError) {
      return validationError;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: formData.displayName.trim(),
          country: formData.country,
          how_found_us: formData.howFoundUs,
          user_type: formData.userType,
          company_name: formData.userType === 'business' ? formData.companyName.trim() : null,
          role_description: formData.roleDescription,
        },
      });

      if (error) {
        throw error;
      }

      try {
        posthog.setPersonProperties({
          full_name: formData.displayName.trim(),
          country: formData.country,
          how_found_us: formData.howFoundUs,
          user_type: formData.userType,
          company_name: formData.userType === 'business' ? formData.companyName.trim() : null,
          role_description: formData.roleDescription,
        });
      } catch (error) {
        Logger.error('Error updating user properties on PostHog', error);
      }

      // Refresh user data to update the auth context
      await refreshUser();

      return null; // Success
    } catch (err) {
      return err instanceof Error ? err.message : 'Failed to save user information';
    } finally {
      setLoading(false);
    }
  };

  return {
    currentStep,
    formData,
    loading,
    isCurrentStepValid,
    isFormValid,
    updateField,
    nextStep,
    prevStep,
    submitForm,
  };
}
